import React, { useState, useEffect, useRef } from 'react';
import {
  Link,
  Flex,
  Box,
  Button,
  Icon,
  Collapse,
  Text,
  Image,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import {
  LuHome,
  LuAlignEndVertical,
  LuAlarmClock,
  LuTowerControl,
  LuSettings,
  LuMenu,
  LuCheck,
} from 'react-icons/lu';
import { RiArrowDownSFill } from 'react-icons/ri';
import { Link as RouterLink } from 'react-router-dom';
import useAuthStore from '../stores/auth.store';
import { FaUser } from 'react-icons/fa';
import useCurrentUser from '../hooks/useCurrentUser';

const pages = [
  {
    name: 'Home',
    href: '',
    icon: LuHome,
    submenus: [],
  },
  {
    name: 'Report',
    href: '/report',
    icon: LuAlignEndVertical,
    submenus: ['Hourly', 'Daily', 'Detailed', 'Custom', 'Settings'],
  },
  {
    name: 'Alarm',
    href: '/alarm',
    icon: LuAlarmClock,
    submenus: ['Display', 'History', 'Settings'],
  },
  {
    name: 'Service',
    href: '/service',
    icon: LuCheck,
    submenus: ['Summary', 'History', 'Settings'],
  },
  {
    name: 'Mimic Screen',
    href: '/flow',
    icon: LuTowerControl,
    submenus: ['Screens', 'Create new'],
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: LuSettings,
    submenus: ['Calculation', 'Devices', 'Tag', 'User'],
  },
];

const ADMIN_ONLY_SUBMENUS = ['User'];

const buildPages = canManageUsers =>
  canManageUsers
    ? pages
    : pages.map(page => ({
        ...page,
        submenus: page.submenus.filter(
          submenu => !ADMIN_ONLY_SUBMENUS.includes(submenu)
        ),
      }));

// for different url
const getSubmenuHref = submenu => {
  return `/${submenu.toLowerCase().replace(/\s+/g, '-')}`;
};

const Sidebar = ({ isOpen, onOpen, onClose }) => {
  const [openMenu, setOpenMenu] = useState(null);
  const menuSectionRef = useRef(null);
  const bottomSectionRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState('auto');
  const logout = useAuthStore(state => state.logout);
  const { displayName, canManageUsers } = useCurrentUser();
  const visiblePages = buildPages(canManageUsers);
  const [isModalOpen, setModalOpen] = useState(false);
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  useEffect(() => {
    if (menuSectionRef.current && bottomSectionRef.current) {
      const calculateAvailableHeight = () => {
        const topOffset = menuSectionRef.current.offsetTop;
        const bottomHeight = bottomSectionRef.current.offsetHeight;
        const newAvailableHeight = `calc(100vh - ${topOffset}px - ${bottomHeight}px)`;
        setAvailableHeight(newAvailableHeight);
      };

      calculateAvailableHeight();

      window.addEventListener('resize', calculateAvailableHeight);

      return () => {
        window.removeEventListener('resize', calculateAvailableHeight);
      };
    }
  }, [menuSectionRef, bottomSectionRef]);

  const handleMenuClick = index => {
    setOpenMenu(openMenu === index ? null : index);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Box bg="sidebar.bg" color="sidebar.text" h="100vh">
      <Flex flexDirection="column" h="100vh">
        {/* Logo and Company Name */}
        <Box
          p={4}
          bg="sidebar.bg"
          color="sidebar.text"
          boxShadow="0 2px 4px rgba(0,0,0,0.1)"
          flexShrink={0}
          position="sticky"
          top="0"
          zIndex="10"
          as={Link}
          to="/"
        >
          <Flex alignItems="center">
            <Image
              src="/logo_r00_draft.png"
              alt="Company Logo"
              boxSize="50px"
              mr={2}
            />
            <Text fontSize="lg" fontWeight="bold">
              Nexa-Flow
            </Text>
          </Flex>
        </Box>

        <Box
          ref={menuSectionRef}
          flex="1"
          bg="sidebar.bg"
          overflowY="scroll"
          pt={0}
          css={{
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0, 0, 0, 0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#A0AEC0',
              borderRadius: '24px',
            },
            scrollbarWidth: 'thin',
            scrollbarColor: '#A0AEC0 rgba(0, 0, 0, 0.05)',
          }}
        >
          <Flex flexDirection="column">
            {visiblePages.map((page, i) => {
              const hasSubmenus = page.submenus && page.submenus.length > 0;
              return (
                <Box key={i}>
                  <Button
                    id={`menu-${i}`}
                    variant="ghost"
                    leftIcon={<Icon as={page.icon} />}
                    my={2}
                    w="full"
                    justifyContent="start"
                    bg="sidebar.bg"
                    color="sidebar.text"
                    _hover={{ bg: 'sidebar.hoverBg' }}
                    {...(!hasSubmenus
                      ? { as: RouterLink, to: page.href || '/' }
                      : {})}
                    onClick={() => handleMenuClick(i)}
                  >
                    {page.name}
                    {hasSubmenus && (
                      <Icon
                        as={openMenu === i ? RiArrowDownSFill : LuMenu}
                        ml="auto"
                      />
                    )}
                  </Button>
                  <Collapse in={openMenu === i}>
                    <Flex flexDirection="column" pl={4}>
                      {page.submenus.map((submenu, index) => {
                        const submenuHref = `${page.href}${getSubmenuHref(submenu)}`;
                        const isNewTab = submenuHref === '/flow/create-new';
                        return (
                          <Button
                            key={index}
                            as={RouterLink}
                            to={submenuHref}
                            {...(isNewTab ? { target: '_blank' } : {})}
                            variant="ghost"
                            justifyContent="start"
                            bg="sidebar.bg"
                            color="sidebar.text"
                            _hover={{ bg: 'sidebar.hoverBg' }}
                          >
                            {submenu}
                          </Button>
                        );
                      })}
                    </Flex>
                  </Collapse>
                </Box>
              );
            })}
          </Flex>
        </Box>

        <Box
          ref={bottomSectionRef}
          pb={4}
          bg="sidebar.bg"
          color="sidebar.text"
          textAlign="center"
          css={{
            borderTop: '2px solid sidebar.border',
          }}
        >
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<FaUser />}
              size="md"
              variant="outline"
              mt={2}
              bg="sidebar.bg"
              color="sidebar.text"
              _hover={{ bg: 'sidebar.hoverBg' }}
            />
            <MenuList bg="sidebar.bg" color="sidebar.text">
              <MenuItem as={RouterLink} to="/profile">
                Profile
              </MenuItem>
              <MenuItem onClick={logout}>Logout</MenuItem>
            </MenuList>
          </Menu>
          {displayName && (
            <Text mt={2} fontSize="sm" fontWeight="bold">
              {displayName}
            </Text>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default Sidebar;
